import { useId } from 'react'

import { cn, insetFocusRing } from './cn'
import { ErrorMark, ErrorsDescription } from './ErrorMark'
import { tabIds, useTabHandlers, type TabItem } from './tab-list'

export interface TabsProps {
  readonly tabs: readonly TabItem[]
  readonly value: string
  readonly onChange: (id: string) => void
  /** The tab list's accessible name. */
  readonly label: string
  /** Links each tab to the panel given `tabPanelProps(idPrefix, id)`. */
  readonly idPrefix?: string
  readonly className?: string
}

/**
 * A horizontal tab strip, 43px tall: the active tab bold in `text-primary`, with a soft green
 * glow, over a 3px accent underline; the rest regular in `text-secondary`. Tabs that do not fit
 * wrap onto another row.
 */
export function Tabs({ tabs, value, onChange, label, idPrefix, className }: TabsProps) {
  const { onClick, onKeyDown } = useTabHandlers(tabs, onChange, 'horizontal')
  const errorsId = useId()

  return (
    <div className={cn('border-b border-divider', className)}>
      <div
        role="tablist"
        aria-label={label}
        aria-orientation="horizontal"
        className="flex flex-wrap gap-x-8"
      >
        {tabs.map((tab) => {
          const isActive = tab.id === value
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              {...tabIds(idPrefix, tab.id)}
              data-tab-id={tab.id}
              aria-selected={isActive}
              aria-describedby={tab.hasErrors === true ? errorsId : undefined}
              tabIndex={isActive ? 0 : -1}
              onClick={onClick}
              onKeyDown={onKeyDown}
              className={cn(
                'flex h-[43px] shrink-0 cursor-pointer items-center whitespace-nowrap border-b-[3px] text-lg transition duration-150',
                insetFocusRing,
                isActive
                  ? 'border-accent-main font-bold text-text-primary [text-shadow:0_0_18px_var(--tms-palette-primary-glow)]'
                  : 'border-transparent font-normal text-text-secondary hover:border-border-strong hover:text-text-primary',
              )}
            >
              {tab.label}
              {tab.hasErrors === true && <ErrorMark />}
            </button>
          )
        })}
      </div>
      {tabs.some((item) => item.hasErrors === true) && <ErrorsDescription id={errorsId} />}
    </div>
  )
}
